import { fetchClientDetail } from "../api";
import type { DataRecord, PaidDetailPayload, SeoDetailPayload } from "../types";

export type ExecutiveSourceState = "REAL" | "PARCIAL" | "PROTOTIPO";
export type ExecutiveSourceKind = "paid" | "seo";

const sourceCache = new Map<string, Promise<PaidDetailPayload | SeoDetailPayload>>();

export function periodToWindow(period: number): string {
  if (period === 7) return "7d";
  if (period === 90) return "90d";
  return "30d";
}

export function loadExecutiveSource(
  clientSlug: string,
  period: number,
  kind: "paid",
): Promise<PaidDetailPayload>;
export function loadExecutiveSource(
  clientSlug: string,
  period: number,
  kind: "seo",
): Promise<SeoDetailPayload>;
export function loadExecutiveSource(
  clientSlug: string,
  period: number,
  kind: ExecutiveSourceKind,
): Promise<PaidDetailPayload | SeoDetailPayload> {
  const window = periodToWindow(period);
  const key = `${clientSlug}:${window}:${kind}`;
  const cached = sourceCache.get(key);
  if (cached) return cached;
  const request = fetchClientDetail(clientSlug, kind, window).catch((error) => {
    sourceCache.delete(key);
    throw error;
  });
  sourceCache.set(key, request);
  return request;
}

export function numberValue(value: unknown): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

export function textValue(value: unknown, fallback = "—"): string {
  if (value === null || value === undefined || value === "") return fallback;
  return String(value);
}

export function recordValue(value: unknown): DataRecord {
  return value && typeof value === "object" && !Array.isArray(value)
    ? value as DataRecord
    : {};
}

export function rowValues(value: unknown): DataRecord[] {
  return Array.isArray(value)
    ? value.filter((item): item is DataRecord => Boolean(item) && typeof item === "object")
    : [];
}

export function percentDelta(current: number, previous: number): number | null {
  if (!previous) return null;
  return (current / previous - 1) * 100;
}

function normalizedPlatform(value: unknown): "meta" | "google" | "other" {
  const platform = textValue(value, "").toLowerCase();
  if (platform.includes("meta") || platform.includes("facebook")) return "meta";
  if (platform.includes("google")) return "google";
  return "other";
}

export function rowsForPlatform(rows: DataRecord[] | undefined, platform?: "meta" | "google"): DataRecord[] {
  if (!platform) return rows ?? [];
  return (rows ?? []).filter((row) => normalizedPlatform(row.platform) === platform);
}

export interface PaidAggregate {
  spend: number;
  conversions: number;
  conversionValue: number;
  cpa: number | null;
  roas: number | null;
}

export function aggregatePaid(
  data: PaidDetailPayload | undefined,
  platform?: "meta" | "google",
): PaidAggregate {
  if (!data) return { spend: 0, conversions: 0, conversionValue: 0, cpa: null, roas: null };
  if (!platform) {
    const summary = recordValue(data.summary);
    return {
      spend: numberValue(summary.spend_total),
      conversions: numberValue(summary.conversions),
      conversionValue: numberValue(summary.conversion_value),
      cpa: summary.cpa === null || summary.cpa === undefined ? null : numberValue(summary.cpa),
      roas: summary.roas === null || summary.roas === undefined ? null : numberValue(summary.roas),
    };
  }
  const rows = rowsForPlatform(data.daily, platform);
  const spend = rows.reduce((sum, row) => sum + numberValue(row.spend_daily), 0);
  const conversions = rows.reduce((sum, row) => sum + numberValue(row.conversions_daily), 0);
  const conversionValue = rows.reduce((sum, row) => sum + numberValue(row.conversion_value_daily), 0);
  return {
    spend,
    conversions,
    conversionValue,
    cpa: conversions ? spend / conversions : null,
    roas: spend ? conversionValue / spend : null,
  };
}

export interface SeoAggregate {
  clicks: number;
  impressions: number;
  ctr: number | null;
  position: number | null;
  top3: number;
  top10: number;
  totalKeywords: number;
  sessions: number;
}

export function aggregateSeo(data: SeoDetailPayload | undefined): SeoAggregate {
  const trend = data?.trend ?? [];
  const clicks = trend.reduce((sum, row) => sum + numberValue(row.clics), 0);
  const impressions = trend.reduce((sum, row) => sum + numberValue(row.impresiones), 0);
  const audit = recordValue(data?.audit);
  const kpis = recordValue(audit.kpis);
  const sessions = (data?.devices ?? []).reduce((sum, row) => sum + numberValue(row.sesiones), 0);
  return {
    clicks,
    impressions,
    ctr: impressions ? clicks / impressions * 100 : null,
    position: kpis.posicionPromedio === null || kpis.posicionPromedio === undefined
      ? null
      : numberValue(kpis.posicionPromedio),
    top3: numberValue(kpis.srTop3),
    top10: numberValue(kpis.srTop10),
    totalKeywords: numberValue(kpis.srTotalKW),
    sessions,
  };
}

export function normalizedSeries(
  rows: DataRecord[],
  labelKey: string,
  barKey: string,
  lineKey: string,
): Array<{ label: string; bar: number; line: number }> {
  const barMax = Math.max(1, ...rows.map((row) => numberValue(row[barKey])));
  const lineMax = Math.max(1, ...rows.map((row) => numberValue(row[lineKey])));
  return rows.map((row) => ({
    label: textValue(row[labelKey]).slice(5, 10),
    bar: numberValue(row[barKey]) / barMax * 100,
    line: numberValue(row[lineKey]) / lineMax * 100,
  }));
}

export function latestTimestamp(values: unknown[]): string | null {
  const timestamps = values
    .map((value) => value ? new Date(String(value)) : null)
    .filter((value): value is Date => value !== null && !Number.isNaN(value.getTime()))
    .sort((left, right) => right.getTime() - left.getTime());
  return timestamps[0]?.toISOString() ?? null;
}
